import moment from 'moment';

import { getDatabaseClient } from '~/data/db/DatabaseClient';
import QueryBuilder from '~/sqlite/services/queryBuilder';

import {
  EnqueueSyncPayload,
  SyncQueueRow,
} from './syncQueueTypes';

export type { EnqueueSyncPayload, SyncQueueItem, SyncQueueRow } from './syncQueueTypes';

export class SyncQueue {
  private qb = new QueryBuilder();

  private getClient() {
    return getDatabaseClient();
  }

  enqueue(items: EnqueueSyncPayload[]): Promise<number | void> {
    const client = this.getClient();
    if (!client || !items.length) {
      return Promise.resolve();
    }
    const rows = items.map((item) => ({
      ...item,
      images: item.images ?? null,
      createdAt: moment.utc().format('YYYY-MM-DD HH:mm:ss'),
    }));
    return new Promise((resolve, reject) => {
      client.transaction(
        (tx) => {
          const { query, params } = this.qb.insert('SyncOfflineData', rows);
          tx.executeSql(
            query,
            params,
            (_tx, result) => {
              resolve(result.insertId);
            },
            (_tx, error) => reject(error)
          );
        },
        reject,
        () => resolve()
      );
    });
  }

  listByUserId(userId: number): Promise<SyncQueueRow[]> {
    const client = this.getClient();
    if (!client) {
      return Promise.resolve([]);
    }
    const where = [{ name: 'userId', value: String(userId) }];
    const orderBy = [
      { name: 'createdAt', value: 'ASC' },
      { name: 'id', value: 'ASC' },
    ];
    const { query, params } = this.qb.select('SyncOfflineData', [], where, orderBy);
    return client
      .executeSql(query, params)
      .then((resultSet) => this.qb.getAllResult(resultSet) as SyncQueueRow[]);
  }

  /**
   * Remove existing queue rows for the user/type that match the predicate, then enqueue.
   * Prevents duplicate server records when connectivity flaps during offline edits.
   */
  async enqueueReplace(
    items: EnqueueSyncPayload[],
    shouldReplace: (existingData: Record<string, unknown>, newData: Record<string, unknown>) => boolean
  ): Promise<number | void> {
    if (!items.length) {
      return;
    }
    const userId = items[0].userId;
    const typeOfData = items[0].typeOfData;
    const rows = await this.listByUserId(userId);
    for (const row of rows) {
      if (row.typeOfData !== typeOfData) {
        continue;
      }
      try {
        const existingData = JSON.parse(row.data) as Record<string, unknown>;
        const newData = JSON.parse(items[0].data) as Record<string, unknown>;
        if (shouldReplace(existingData, newData)) {
          await this.removeById(row.id);
        }
      } catch {
        /* ignore malformed row */
      }
    }
    return this.enqueue(items);
  }

  removeById(id: number): Promise<void> {
    const client = this.getClient();
    if (!client) {
      return Promise.resolve();
    }
    const { query, params } = this.qb.delete('SyncOfflineData', [
      { name: 'id', value: String(id) },
    ]);
    return client.transactionAsync((tx) => {
      tx.executeSql(query, params);
    });
  }

  removeAllForUser(userId: number): Promise<void> {
    const client = this.getClient();
    if (!client) {
      return Promise.resolve();
    }
    const { query, params } = this.qb.delete('SyncOfflineData', [
      { name: 'userId', value: String(userId) },
    ]);
    return client.transactionAsync((tx) => {
      tx.executeSql(query, params);
    });
  }
}

export const syncQueue = new SyncQueue();
